import * as React from "react";
import { Button, Container, Form, FormGroup, FormRadio, Segment } from "@/components/bp/bp-ui";
import { FormikProps } from "formik";
import { SampleUpdateRequest } from "@app/api2/api";
import { PageHeader } from "@common/PageHeader";
import { insertComponentsIntoText } from "@common/Utils";
import { buildUrl, AppRoutes } from "../AppRoutes";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import BPLoading from "../Common/BPLoading";
import { FormikWithSchema } from "../Common/Forms/FormikWithSchema";
import SemanticFormikInput from "../Common/Forms/SemanticFormikInput";
import { useTranslation2 } from "../Contexts/TranslationContext2";
import { useKits, useSex } from "../Hooks/UseKits";
import SemanticFormikSelect from "../Common/Forms/SemanticFormikSelect";
import { SampleEditSchema, SampleUpdateRequestWithDobMode } from "./SampleEdit.Schema";
import { sampleClient } from "@app/api2/Clients";
import { useNavigate } from "react-router";
import { BPButton } from "@/components/bp/bpbutton";
import { BPContainer } from "@/components/bp/bpcontainer";

export class SampleEditProps {
    public sampleId: number;
    public labId: number;
    public caseId?: number;
    public dialogMode?: boolean = false;
    public onSave?: () => void;
    public onCancel?: () => void;
}

export const SampleEdit: React.FC<SampleEditProps> = (props) => {
    const { translations: t } = useTranslation2();
    const navigate = useNavigate();

    const sampleQuery = useQuery({
        queryKey: ['sample', props.labId, props.caseId, props.sampleId],
        queryFn: async () => {
            if (props.sampleId) {
                return await sampleClient.getById(props.labId, props.caseId ?? 0, props.sampleId);
            }
        }
    });

    const kits = useKits({ labId: props.labId, defaultId: sampleQuery.data?.sample.kitId });
    const sex = useSex({ defaultId: sampleQuery.data?.sample.sex });


    const sampleMutation = useMutation({
        mutationFn: async (values: SampleUpdateRequestWithDobMode) => {
            if (values.dobMode === 'date') {
                values.ageMonths = null;
                values.ageYears = null;
            } else {
                values.birthYear = null;
                values.birthMonth = null;
                values.birthDay = null;
            }
            return await sampleClient.update(props.labId, props.caseId ?? 0, props.sampleId, values);
        }
    });

    if (sampleQuery.isLoading) {
        return <BPLoading></BPLoading>;
    }

    const dobMode = sampleQuery.data?.sample.ageMonths || sampleQuery.data?.sample.ageYears ? 'age' : 'date';

    const initialData = {
        kitId: sampleQuery.data.sample.kitId || '',
        name: sampleQuery.data.sample.name || '',
        dateOfBirth: sampleQuery.data.sample.dateOfBirth || null,
        sex: sampleQuery.data.sample.sex || '',
        labId: props.labId || '',
        sampleId: props.sampleId || '',
        ageMonths: sampleQuery.data.sample.ageMonths || '',
        ageYears: sampleQuery.data.sample.ageYears || '',
        birthDay: sampleQuery.data.sample.birthDay || '',
        birthMonth: sampleQuery.data.sample.birthMonth || '',
        birthYear: sampleQuery.data.sample.birthYear || '',
        dobMode: dobMode || 'date',
    } as SampleUpdateRequestWithDobMode;

    return <>
        <BPContainer>
            {!props.dialogMode && <PageHeader header={insertComponentsIntoText(window.translation.sampleView.editTitle, {
                '{sampleName}': sampleQuery.data?.sample?.name,
                '{caseName}': <Link key={'caseLink'} to={buildUrl(AppRoutes.CaseDetails, {
                    caseId: props.caseId,
                    labId: props.labId
                })}>{sampleQuery.data.sample.cases?.length > 0 ? sampleQuery.data.sample.cases[0].name : ''}</Link>
            })} />}
            <FormikWithSchema
                validationSchema={SampleEditSchema(t)}
                initialValues={initialData}
                onSubmit={(values, helpers) => {
                    sampleMutation.mutate(values, {
                        onSuccess: (result) => {
                            sampleQuery.refetch();
                            if (result.success) {
                                if (props.dialogMode)
                                    props.onSave?.();
                                else
                                    navigate(props.caseId ? buildUrl(AppRoutes.SampleDetails, { caseId: props.caseId, labId: props.labId, sampleId: props.sampleId }) : buildUrl(AppRoutes.SampleDetailsWithoutCase, { labId: props.labId, sampleId: props.sampleId }));

                                    // navigate(result.url);
                            }
                        },
                        onError: () => {
                            helpers.setSubmitting(false);
                        }
                    });
                }}>
                {(formikProps: FormikProps<SampleUpdateRequestWithDobMode>) => (
                    <Segment>
                        <Form loading={formikProps.isSubmitting} onSubmit={formikProps.handleSubmit}>
                            <SemanticFormikSelect name="kitId" {...kits.getDropdownProps()} />
                            <SemanticFormikInput name="name" />
                            <SemanticFormikSelect name="sex" {...sex.getDropdownProps()} />
                            {/* <SemanticFormikDatePicker name="dateOfBirth" /> */}
                            <FormGroup inline>
                                <label>{t.sample.age}</label>
                                <FormRadio
                                    label={t.sample.dateOfBirth}
                                    value='date'
                                    checked={formikProps.values.dobMode === 'date'}
                                    onChange={() => formikProps.setFieldValue('dobMode', 'date')}
                                />
                                <FormRadio
                                    label={t.sample.age}
                                    value='age'
                                    checked={formikProps.values.dobMode === 'age'}
                                    onChange={() => formikProps.setFieldValue('dobMode', 'age')}
                                />
                            </FormGroup>
                            {formikProps.values.dobMode === 'age' && <FormGroup widths="1">
                                <SemanticFormikInput name="ageYears" type="number" label={t.sample.ageYears} />
                                <SemanticFormikInput name="ageMonths" type="number" label={t.sample.ageMonths} />
                            </FormGroup>}
                            {formikProps.values.dobMode === 'date' && <FormGroup widths="1">
                                <SemanticFormikInput name="birthDay" type="number" label={t.sample.birthDay} />
                                <SemanticFormikInput name="birthMonth" type="number" label={t.sample.birthMonth} />
                                <SemanticFormikInput name="birthYear" type="number" label={t.sample.birthYear} />
                            </FormGroup>}
                            <BPButton type="submit" disabled={!formikProps.isValid || formikProps.isSubmitting}>{t.common.save}</BPButton>
                            {props.dialogMode && <BPButton type="button" onClick={props.onCancel}>{t.common.cancel}</BPButton>}
                        </Form>
                    </Segment>
                )}
            </FormikWithSchema>
        </BPContainer>
    </>
};

